import React from "react";
import { FiUnlock, FiX } from "react-icons/fi";
import { createLogger } from "../../lib/logger";

const log = createLogger("WeekUnlockPopup");

interface WeekUnlockPopupProps {
  /** The week number that was just unlocked */
  weekNumber: number;
  /** Callback to dismiss the popup */
  onDismiss: () => void;
  /** Optional callback to navigate to the unlocked week */
  onGoToWeek?: (week: number) => void;
}

const WeekUnlockPopup: React.FC<WeekUnlockPopupProps> = ({
  weekNumber,
  onDismiss,
  onGoToWeek,
}) => {
  return (
    /* Backdrop */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4"
      onClick={onDismiss}
    >
      {/* Card (stop propagation so clicking the card doesn't dismiss) */}
      <div
        className="relative w-full max-w-sm bg-white rounded-3xl shadow-xl p-8 text-center animate-fade-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Close"
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 border-none cursor-pointer transition-colors"
        >
          <FiX size={16} className="text-gray-500" />
        </button>

        {/* Unlock icon */}
        <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-brand-light flex items-center justify-center">
          <FiUnlock size={28} className="text-brand-primary" />
        </div>

        {/* Title */}
        <h2 className="m-0 text-2xl font-bold text-brand-dark font-dm-sans">
          Week {weekNumber} Ready!
        </h2>

        {/* Body */}
        <p className="mt-3 mb-6 text-sm text-gray-600 font-dm-sans leading-relaxed">
          Well done! You&rsquo;ve completed enough lessons and time has
          passed. Unlock the next week when you&rsquo;re ready.
        </p>

        {/* Actions */}
        <div className="flex flex-col gap-3">
          {onGoToWeek && (
            <button
              type="button"
              onClick={() => {
                log.info("Week unlock confirmed", { weekNumber });
                onGoToWeek(weekNumber);
              }}
              className="w-full py-3 rounded-full bg-brand-primary text-white text-sm font-semibold border-none cursor-pointer hover:opacity-90 transition-opacity font-dm-sans"
            >
              Unlock Week {weekNumber}
            </button>
          )}
          <button
            type="button"
            onClick={() => { log.info("Week unlock dismissed", { weekNumber }); onDismiss(); }}
            className="w-full py-3 rounded-full bg-gray-100 text-gray-700 text-sm font-medium border-none cursor-pointer hover:bg-gray-200 transition-colors font-dm-sans"
          >
            Stay on this week
          </button>
        </div>
      </div>
    </div>
  );
};

export default WeekUnlockPopup;
